import type { UniversalMoney } from '@dutchinvoice/shared-utils';
import { moneyUtils as sharedMoneyUtils } from '@dutchinvoice/shared-utils';
import { useTranslation } from '../hooks/useTranslation';

// Re-export the type from shared-utils
export type { UniversalMoney };
export type MoneyInCents = number;

/**
 * Core money utilities for handling money values
 * 
 * These functions handle conversion between different formats:
 * - Universal format: "1234.56" (always uses dot as decimal separator)
 * - User input: Can be "1234,56" or "1234.56" depending on locale
 * - Display format: Formatted according to locale (e.g., "€ 1.234,56" for Dutch)
 */
// Re-export the shared utils
export const moneyUtils = sharedMoneyUtils;

/**
 * Format a number without trailing zeros
 * e.g., 5.00 -> "5", 5.50 -> "5.5", 5.25 -> "5.25"
 */
export const formatNumberWithoutTrailingZeros = (value: string | number): string => {
  // Convert to number and back to string to normalize
  const num = parseFloat(String(value)) || 0;

  // If it's a whole number, return without decimal places
  if (Number.isInteger(num)) {
    return num.toString();
  }

  // Otherwise, return with decimal places but trim trailing zeros
  return num.toString().replace(/\.?0+$/, '');
};

/**
 * Hook for display formatting - Only used in UI components
 * Uses interface language, not invoice language
 */
export const useMoneyDisplay = () => {
  const { locale } = useTranslation();

  return {
    /**
     * Format universal money for display without currency symbol
     */
    formatMoney: (value: UniversalMoney): string => {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },

    /**
     * Format with currency symbol
     */
    formatCurrency: (value: UniversalMoney): string => {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : 'en-US', {
        style: 'currency',
        currency: 'EUR',
      }).format(amount);
    },

    /**
     * Parse user input back to universal format
     */
    parseUserInput: (input: string): UniversalMoney => {
      return moneyUtils.toUniversal(input);
    },

    /**
     * Format a number (like quantity or VAT rate) without trailing zeros
     */
    formatNumber: (value: string | number): string => {
      return formatNumberWithoutTrailingZeros(value);
    },
  };
};

// For backward compatibility - alias to useMoneyDisplay
export const useMoneyFormatters = useMoneyDisplay;

/**
 * Standalone formatter for use outside of React components
 * Formats a value as currency using Dutch locale (default)
 */
export const formatMoney = (value: UniversalMoney | string | number): string => {
  const universalValue = moneyUtils.toUniversal(value);
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parseFloat(universalValue));
};